/**
 * The expanded drill-down panel for one delivery leg: the headline facts from
 * the projection, the note-by-note journey trail from the reconstruction
 * endpoint, and a one-click "Explain" that asks Zoe for a plain-English
 * account (cached).
 */

import { useState } from 'react';
import { AlertTriangle, Loader2, MessageSquareText } from 'lucide-react';
import ZoeMark from '../zoeAssistant/ZoeMark';
import { useOptionalZoeControls } from '../../contexts/ZoeConversationContext';
import type { OrdersDailyRow } from '../../types/ordersDailyTruth';
import { journeyTrailSummary, statusCodeLabel } from '../../utils/ordersDailyPresentation';
import { formatUkDateTime } from '../../lib/calc';
import { useOrderEvidence, type OrderEvidenceNote } from '../../hooks/useOrderEvidence';
import { explainOrder, readCachedNarrative, type OrderNarrative } from '../../services/orderNarrativeApi';

interface OrderJourneyTrailProps {
  row: OrdersDailyRow;
}

function fmtWhen(iso: string | undefined): string {
  if (!iso) return '';
  return formatUkDateTime(iso, iso);
}

type TrailEntry =
  | { kind: 'note'; ts: number; key: string; note: OrderEvidenceNote }
  | { kind: 'delivered'; ts: number; key: string; event: Record<string, unknown> };

/**
 * One time-ordered list from the note stream and the OrderDelivered events.
 * The endpoint returns them as two arrays; rendered separately the POD ends up
 * below a later scan, which makes a re-opened leg read as delivered. Undated
 * rows sort to the end, stable within their original order.
 */
function mergeTrail(
  notes: readonly OrderEvidenceNote[],
  deliveries: readonly Record<string, unknown>[],
): TrailEntry[] {
  const parse = (v: unknown): number => {
    const t = Date.parse(String(v ?? ''));
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  const entries: TrailEntry[] = [
    ...notes.map((note, i): TrailEntry => ({
      kind: 'note', ts: parse(note.occurredAt), key: note.rawRef ?? `note-${i}`, note,
    })),
    ...deliveries.map((event, i): TrailEntry => ({
      kind: 'delivered', ts: parse(event.occurred_at), key: `del-${i}`, event,
    })),
  ];
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.ts === b.e.ts ? a.i - b.i : a.e.ts - b.e.ts))
    .map(({ e }) => e);
}

export default function OrderJourneyTrail({ row }: OrderJourneyTrailProps) {
  const ref = row.tracking_id;
  const { data, loading, error } = useOrderEvidence(ref, row.grain_date, true);
  const summary = journeyTrailSummary(row);

  const [narrative, setNarrative] = useState<OrderNarrative | null>(() => readCachedNarrative(ref));
  const [narrating, setNarrating] = useState(false);
  const [narrateError, setNarrateError] = useState<string | null>(null);
  // Absent outside the app shell (tests/previews) — the hand-off link just doesn't render.
  const zoe = useOptionalZoeControls();

  /** Carry this order into ZOE, in the pop-up, so follow-up questions happen
   *  in one place rather than in two ZOEs. A new conversation: the order must
   *  not inherit whatever the open one was about (the old one stays in History). */
  const continueInZoe = () => {
    if (!zoe?.popupAvailable) return;
    zoe.setPopupOpen(true);
    // Same wording Explain sends (orderNarrativeApi), so the conversation
    // starts from the question the summary above answered.
    void zoe.ask(`What happened to order ${ref}?`, { newConversation: true });
  };

  const runExplain = async (force: boolean) => {
    setNarrating(true);
    setNarrateError(null);
    try {
      setNarrative(await explainOrder(ref, force));
    } catch (err) {
      setNarrateError(err instanceof Error ? err.message : 'Could not generate an explanation');
    } finally {
      setNarrating(false);
    }
  };

  const notes = data?.evidence.operatorNotes ?? [];
  const deliveries = data?.evidence.events.delivered ?? [];

  return (
    <div className="px-4 py-4 bg-background/40 border-l-2 border-brand/40 space-y-4 text-sm">
      {summary.length > 0 && (
        <ul className="space-y-1">
          {summary.map((line, i) => (
            <li key={i} className="flex items-start gap-2 text-warning/90">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}

      {/* ── Zoe "Explain" ── */}
      <div className="rounded-lg border border-zoe-500/30 bg-zoe-900/10 p-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-zoe-300/80 flex items-center gap-1.5">
            <ZoeMark className="w-3.5 h-3.5" aria-hidden /> Explain
          </span>
          <button
            type="button"
            onClick={() => runExplain(narrative != null)}
            disabled={narrating}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-zoe-600 hover:bg-zoe-700 disabled:opacity-50 text-white transition-colors"
          >
            {narrating && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />}
            {narrating ? 'Asking ZOE…' : narrative ? 'Regenerate' : 'Ask ZOE'}
          </button>
        </div>
        {narrateError && <p className="mt-2 text-xs text-destructive">{narrateError}</p>}
        {narrative && (
          <div className="mt-2 space-y-1">
            <p className="text-foreground">{narrative.summary}</p>
            {narrative.detail && <p className="text-muted-foreground text-xs whitespace-pre-line">{narrative.detail}</p>}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">Generated {fmtWhen(narrative.computedAt)} · cached</p>
              {zoe?.popupAvailable ? (
                <button
                  type="button"
                  onClick={continueInZoe}
                  disabled={zoe.isLoading}
                  title={zoe.isLoading ? 'ZOE is still answering another question' : undefined}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-zoe-300 hover:text-zoe-200 hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  Continue in ZOE
                  <ZoeMark className="w-3.5 h-3.5" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* ── Journey trail ── */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
          <MessageSquareText className="w-3.5 h-3.5" aria-hidden /> Journey trail
        </div>
        {loading && (
          <p className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Loading evidence…
          </p>
        )}
        {error && <p className="text-destructive text-xs">{error}</p>}
        {!loading && !error && notes.length === 0 && deliveries.length === 0 && (
          <p className="text-muted-foreground text-xs">No note stream or delivery events recorded for this order.</p>
        )}
        {!loading && !error && (notes.length > 0 || deliveries.length > 0) && (
          <ol className="space-y-1.5 border-l border-border pl-3">
            {mergeTrail(notes, deliveries).map((entry) => {
              if (entry.kind === 'note') {
                const n = entry.note;
                const label = statusCodeLabel(n.statusCode);
                return (
                  <li key={entry.key} className="relative">
                    <span className="absolute -left-4 top-1.5 w-1.5 h-1.5 rounded-full bg-accent" aria-hidden />
                    <span className="text-muted-foreground tabular-nums mr-2">{fmtWhen(n.occurredAt)}</span>
                    {label && (
                      <span className="mr-2 inline-flex px-1.5 py-0.5 rounded bg-secondary/60 text-foreground text-xs font-semibold">
                        {label}
                      </span>
                    )}
                    <span className="text-foreground">{n.text}</span>
                    {(n.repeatCount ?? 1) > 1 && (
                      <span className="ml-2 text-xs text-muted-foreground" title="Near-identical repeat scans folded in">
                        ×{n.repeatCount}
                      </span>
                    )}
                  </li>
                );
              }
              const pod = (entry.event.payload as Record<string, unknown> | undefined)?.pod_reference;
              return (
                <li key={entry.key} className="relative">
                  <span className="absolute -left-4 top-1.5 w-1.5 h-1.5 rounded-full bg-success" aria-hidden />
                  <span className="text-muted-foreground tabular-nums mr-2">{fmtWhen(String(entry.event.occurred_at ?? ''))}</span>
                  <span className="mr-2 inline-flex px-1.5 py-0.5 rounded bg-success/15 text-success text-xs font-semibold">
                    OrderDelivered
                  </span>
                  <span className="text-foreground">{pod ? `POD reference ${String(pod)}` : 'Delivery confirmed'}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
